/**
 * ChurnRiskCard — the explainable replacement for the opaque health number.
 *
 * Renders, per account, the fused churn probability with:
 *   • per-dimension sparklines (real weekly risk trends, inline SVG),
 *   • ▲/▼ delta-vs-last-week badges per dimension,
 *   • the top-3 causal driver narratives (explainDrivers),
 *   • the falsifiable outcome-contract statement each score stakes,
 *   • a precision header reporting hit-rate over CLOSED contracts only.
 *
 * Agentic seam: the optional `narrate` prop lets the view route "Explain"
 * clicks through the real Argo worker; when the worker is unconfigured or
 * fails, the card gracefully shows the deterministic narrative already
 * carried on the result — honestly labelled as such. Pure presentation
 * otherwise: all math lives in churnRisk.ts.
 */

import { useState } from 'react';
import { Activity, Bot, Gauge, Target, TrendingDown, TrendingUp } from 'lucide-react';
import { GlassPanel } from '../../components/GlassPanel';
import { C } from '../tokens';
import {
  explainDrivers,
  toOutcomeContract,
  RISK_DIMENSION_LABELS,
  type ChurnRiskResult,
  type PredictionPrecision,
  type RiskDimension,
} from './churnRisk';

export interface ChurnRiskCardProps {
  /** Fused per-account results, riskiest first (caller sorts). */
  results: ChurnRiskResult[];
  /** Precision over closed contracts; null = not yet computable. */
  precision?: PredictionPrecision | null;
  /** True while the underlying snapshots are still loading. */
  loading?: boolean;
  /**
   * Route an "Explain" click through the real agent worker. Should resolve
   * to narrative text; the card falls back to the deterministic narrative
   * on rejection. Omit to always use the deterministic path.
   */
  narrate?: (result: ChurnRiskResult) => Promise<string>;
}

/** Risk → display color: hot = magenta, warm = orange, cool = green. */
function riskColor(probability: number): string {
  if (probability >= 60) return C.magenta;
  if (probability >= 35) return C.orange;
  return C.green;
}

/** Tiny pure-SVG sparkline over a 0–100 risk series (higher = worse). */
function Sparkline({ trend, color }: { trend: number[]; color: string }) {
  if (trend.length < 2) {
    return (
      <span className="t-meta" style={{ fontSize: 10, opacity: 0.6 }}>
        no trend
      </span>
    );
  }
  const w = 64;
  const h = 18;
  const step = w / (trend.length - 1);
  const points = trend
    .map((v, i) => `${(i * step).toFixed(1)},${(h - (v / 100) * (h - 2) - 1).toFixed(1)}`)
    .join(' ');
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      aria-label={`trend ${trend.join(', ')}`}
      role="img"
      style={{ display: 'block' }}
    >
      <polyline points={points} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" />
    </svg>
  );
}

/** ▲/▼ delta-vs-last-week badge. Rising risk is bad → magenta ▲. */
function DeltaBadge({ delta }: { delta: number }) {
  if (delta === 0) {
    return (
      <span className="t-meta" style={{ fontSize: 10, opacity: 0.6 }} title="No change vs last week">
        —
      </span>
    );
  }
  const rising = delta > 0;
  return (
    <span
      className={`glass-pill ${rising ? 'glass-pill--magenta' : 'glass-pill--green'}`}
      style={{ fontSize: 10, padding: '1px 6px' }}
      title={`Risk ${rising ? 'up' : 'down'} ${Math.abs(delta)} vs last week`}
    >
      {rising ? '▲' : '▼'} {Math.abs(delta)}
    </span>
  );
}

function DimensionRow({ dim }: { dim: RiskDimension }) {
  const color = riskColor(dim.score);
  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '3px 0',
        opacity: dim.missing ? 0.45 : 1,
      }}
    >
      <span className="t-meta" style={{ flex: '0 0 128px', fontSize: 11 }}>
        {RISK_DIMENSION_LABELS[dim.key]}
      </span>
      <span style={{ flex: '0 0 64px' }}>
        <Sparkline trend={dim.trend} color={color} />
      </span>
      <span
        style={{
          flex: '0 0 34px',
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 600,
          fontSize: 12,
          color,
        }}
        title={dim.missing ? 'No signal measured — neutral placeholder' : `Risk ${dim.score}/100 · weight ${Math.round(dim.weight * 100)}%`}
      >
        {dim.missing ? '·' : dim.score}
      </span>
      <DeltaBadge delta={dim.deltaVsLastWeek} />
      {dim.detail && (
        <span className="t-meta" style={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {dim.detail}
        </span>
      )}
    </li>
  );
}

function AccountRow({
  result,
  narrate,
}: {
  result: ChurnRiskResult;
  narrate?: (result: ChurnRiskResult) => Promise<string>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [narrative, setNarrative] = useState<{ text: string; source: 'agent' | 'deterministic' } | null>(null);
  const [narrating, setNarrating] = useState(false);

  const color = riskColor(result.churnProbability);
  const drivers = explainDrivers(result).filter((d) => d.contribution > 0);
  const contract = toOutcomeContract(result.accountId, result);

  async function onExplain() {
    setNarrating(true);
    try {
      if (narrate) {
        const text = await narrate(result);
        setNarrative(
          text && text !== result.narrative
            ? { text, source: 'agent' }
            : { text: result.narrative, source: result.narrativeSource },
        );
      } else {
        setNarrative({ text: result.narrative, source: result.narrativeSource });
      }
    } catch {
      // Worker unreachable → graceful deterministic fallback, honestly labelled.
      setNarrative({ text: result.narrative, source: 'deterministic' });
    } finally {
      setNarrating(false);
    }
  }

  return (
    <li className="glass-row" style={{ padding: '10px 12px', display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          aria-hidden="true"
          style={{ width: 8, height: 8, borderRadius: 999, background: color, boxShadow: `0 0 8px ${color}`, flex: '0 0 auto' }}
        />
        <span
          style={{ flex: '0 0 44px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color }}
          title={`Churn probability ${result.churnProbability}/100 · confidence ${Math.round(result.confidence * 100)}%`}
        >
          {result.churnProbability}%
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span className="t-card-title" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {result.accountName ?? result.accountId}
          </span>
          <span className="t-meta" style={{ fontSize: 11 }}>
            confidence {Math.round(result.confidence * 100)}% · ~{result.leadTimeDays}d runway
          </span>
        </span>
        <button
          className="glass-btn"
          onClick={() => void onExplain()}
          disabled={narrating}
          title="Narrate this risk — routes through the Argo worker when configured, deterministic drivers otherwise"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
        >
          <Bot size={12} /> {narrating ? 'Explaining…' : 'Explain'}
        </button>
        <button className="glass-btn" onClick={() => setExpanded((e) => !e)} aria-expanded={expanded}>
          {expanded ? 'Hide dims' : 'Dimensions'}
        </button>
      </div>

      {/* Top-3 causal drivers — the actionable "why". */}
      {drivers.length > 0 ? (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 2 }}>
          {drivers.map((d) => (
            <li key={d.key} className="t-meta" style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 6 }}>
              {d.deltaVsLastWeek > 0 ? (
                <TrendingUp size={11} color={C.magenta} aria-hidden="true" />
              ) : (
                <TrendingDown size={11} color={C.green} aria-hidden="true" />
              )}
              {d.text}
            </li>
          ))}
        </ul>
      ) : (
        <span className="t-meta" style={{ fontSize: 11 }}>
          No active risk drivers — steady account.
        </span>
      )}

      {/* The falsifiable claim this score stakes. */}
      <div className="t-meta" style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Target size={11} aria-hidden="true" />
        <span>
          Contract: {contract.statement}
          {contract.dollarsAtStake > 0 &&
            ` · $${contract.dollarsAtStake.toLocaleString('en-US')} at stake`}
        </span>
      </div>

      {narrative && (
        <div className="t-meta" style={{ fontSize: 11, display: 'flex', gap: 6 }}>
          <Bot size={11} style={{ flex: '0 0 auto', marginTop: 1 }} aria-hidden="true" />
          <span>
            {narrative.text}{' '}
            <span style={{ opacity: 0.6 }}>
              ({narrative.source === 'agent' ? 'Argo worker' : 'deterministic fallback'})
            </span>
          </span>
        </div>
      )}

      {expanded && (
        <ul style={{ listStyle: 'none', margin: 0, padding: '4px 0 0', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          {result.dimensions.map((dim) => (
            <DimensionRow key={dim.key} dim={dim} />
          ))}
        </ul>
      )}
    </li>
  );
}

export function ChurnRiskCard({ results, precision, loading, narrate }: ChurnRiskCardProps) {
  const openContracts = results.length;
  return (
    <GlassPanel variant="default" style={{ padding: '18px 20px' }}>
      <div className="dash-section-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            aria-hidden="true"
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              display: 'inline-grid',
              placeItems: 'center',
              background: 'linear-gradient(135deg, rgba(255,0,255,0.18), rgba(0,130,243,0.10))',
              border: '1px solid rgba(255,255,255,0.14)',
              color: C.magenta,
            }}
          >
            <Gauge size={17} strokeWidth={2} />
          </span>
          <div>
            <span className="t-eyebrow">Churn risk — fused</span>
            <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
              Explainable churn risk
            </h2>
          </div>
        </div>
        {/* Precision header — the honest predict-then-verify report. */}
        <span className="glass-pill" title="Hit-rate over CLOSED outcome contracts only — open claims never grade themselves">
          {precision && precision.sampleSize > 0
            ? `Precision ${Math.round(precision.hitRate * 100)}% over ${precision.sampleSize} closed`
            : `No closed predictions yet · ${openContracts} open contract${openContracts === 1 ? '' : 's'}`}
        </span>
      </div>

      <div className="t-meta" style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 10px' }}>
        <Activity size={12} aria-hidden="true" />
        <span>
          Seven dimensions fused per account — usage, tickets, engagement, deal momentum, margin,
          deliverables, champion — every point itemized, every claim falsifiable.
        </span>
      </div>

      {loading ? (
        <div className="t-meta" style={{ padding: '16px 0' }}>
          Loading live signals…
        </div>
      ) : results.length === 0 ? (
        <div className="crm-empty" style={{ padding: '20px 0' }}>
          <Gauge size={20} strokeWidth={1.75} />
          <span>No live customers to score yet — convert a prospect to start the risk engine.</span>
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
          {results.map((r) => (
            <AccountRow key={r.accountId} result={r} narrate={narrate} />
          ))}
        </ul>
      )}
    </GlassPanel>
  );
}
